import { ClickAwayListener } from "@mui/base";
import {
  Deselect,
  FileDownload,
  PlaylistAdd,
  RestoreFromTrash,
  ViewWeekOutlined,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dropdown,
  IconButton,
  ListItem,
  ListItemContent,
  ListItemDecorator,
  Menu,
  MenuButton,
  MenuItem,
  Stack,
  Typography,
} from "@mui/joy";
import { getCoreRowModel, getSortedRowModel, Table, useReactTable } from "@tanstack/react-table";
import { MouseEvent, useCallback, useMemo, useState } from "react";
import { useShallow } from "zustand/react/shallow";

import { Route } from "@/core/types";
import { useExportRoutesMutation } from "@/hooks/routes";
import { useCurrentClimbType } from "@/hooks/venues";

import { useModal } from "../modals/useModal";
import ActiveFiltersIndicator from "../RoutesTable/ActiveFiltersIndicator";
import * as columns from "../RoutesTable/columnDefs";
import { useVirtualElement } from "../RoutesTable/ContextMenu";
import { useRoutesTableStore } from "../RoutesTable/RoutesProvider2";
import RoutesTable from "../RoutesTable/RoutesTableGeneric";
import {
  filterParamsToColumnFilters,
  MultipleChoiceFilter,
} from "../RoutesTable/useFilteredRoutes";
import { useArchiveQueryContext } from "./ArchiveRoutesQueryContext";
import { useArchiveUiStore } from "./ArchiveUiStore";

export const multiChoiceSetToArrayParam = <T,>(v: MultipleChoiceFilter<T> | undefined) => {
  if (!v) {
    return undefined;
  }
  return Array.from(v.matches).filter((x) => !!x) as string[];
};

const emptyArray = [] as never[];
const emptySet = new Set<never>();

export default function ArchiveRoutesTable() {
  const climbType = useCurrentClimbType();

  const {
    selectedRouteUuids,
    setSelectedRouteUuids,
    filterParams,
    // canChangeSelection,
    addToSelection,
    removeFromSelection,
  } = useRoutesTableStore(
    useShallow((state) => ({
      selectedRouteUuids: state.selectedRouteUuids,
      setSelectedRouteUuids: state.setSelectedRouteUuids,
      filterParams: state.filterParams,
      addToSelection: state.addToSelection,
      removeFromSelection: state.removeFromSelection,
    })),
  );

  const {
    data: archiveData,
    isPending,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useArchiveQueryContext();

  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
    routeUuid: string;
  } | null>(null);

  const onContextMenu = useCallback((e: MouseEvent) => {
    e.preventDefault();
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      routeUuid: (e.currentTarget as HTMLElement).dataset.uuid as string,
    });
  }, []);

  const routes = archiveData?.routes || emptyArray;

  const onEndReached = useCallback(() => {
    if (!isFetching && hasNextPage) {
      fetchNextPage();
    }
  }, [isFetching, fetchNextPage, hasNextPage]);

  const tableColumns = useMemo(
    () => [
      columns.selectionColumn,
      columns.archiveRouteColumn,
      ...(climbType === "BOULDER" ? [columns.circuitColumn] : [columns.lineColumn]),
      columns.gradeColumn,
      columns.setterColumn,
      columns.mapColumn,
      climbType === "BOULDER" ? columns.zoneColumn : columns.sportZoneColumn,
      ...(climbType === "SPORT" ? [columns.ascentStyleColumn] : []),
      columns.competitionCategoriesColumn,
      columns.startColumn,
      columns.lifetimeColumn,
      columns.stripColumn,
      columns.starRatingColumn,
      columns.ticksColumn,
      columns.stylesColumn,
      columns.skillsColumn,
      columns.holdsColumn,
      ...(climbType === "SPORT" ? [columns.quickdrawCountColumn, columns.heightColumn] : []),
    ],
    [climbType],
  );

  //convert the selected routes set to an object for react-table
  const tableRowSelection = useMemo(() => {
    return Object.fromEntries(Array.from(selectedRouteUuids, (uuid) => [uuid, true])) as Record<
      string,
      boolean
    >;
  }, [selectedRouteUuids]);

  const [sortState, setSortState] = useArchiveUiStore(
    useShallow((state) => [state.sortState, state.setSortState]),
  );

  const table = useReactTable({
    getRowId: (row) => row.uuid,
    data: routes || emptyArray,
    columns: tableColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualFiltering: true,
    manualSorting: true,
    enableSortingRemoval: false,
    initialState: {
      sorting: [
        {
          id: "endTime",
          desc: true,
        },
      ],
      columnVisibility: {
        [columns.stylesColumn.id || ""]: false,
        [columns.skillsColumn.id || ""]: false,
        [columns.holdsColumn.id || ""]: false,
        [columns.quickdrawCountColumn.id || ""]: false,
        [columns.heightColumn.id || ""]: false,
      },
    },
    state: {
      rowSelection: tableRowSelection,
      sorting: sortState,
      columnFilters: filterParamsToColumnFilters(filterParams, tableColumns),
    },
    onSortingChange: (updater) => {
      const newSortState = updater instanceof Function ? updater(sortState) : updater;
      setSortState(newSortState);
    },
    enableRowSelection: true,
    onRowSelectionChange: (updater) => {
      const selectedRowIds = updater instanceof Function ? updater(tableRowSelection) : updater;
      setSelectedRouteUuids(
        Object.entries(selectedRowIds)
          .filter(([, v]) => v)
          .map(([k]) => k),
      );
    },
  });

  const renderEndRow = useCallback(() => {
    if (isFetchingNextPage) {
      return (
        <Stack alignItems="center" direction="row" spacing={0.5}>
          <CircularProgress size="sm" />
          <Typography>Loading routes...</Typography>
        </Stack>
      );
    }
  }, [isFetchingNextPage]);

  return (
    <Stack sx={{ height: "100%" }}>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          overflow: "hidden",
          minHeight: 0,
          "@media (pointer:coarse)": {
            userSelect: "none",
          },
        }}
      >
        <TableControls table={table} />
        <RoutesTable
          table={table}
          canChangeSelection={true}
          selection={selectedRouteUuids}
          addToSelection={addToSelection}
          removeFromSelection={removeFromSelection}
          setSelection={setSelectedRouteUuids}
          onContextMenu={onContextMenu}
          isDataPending={isPending}
          onCannotChangeSelection={() => {}}
          highlightedRouteUuids={emptySet}
          onEndReached={onEndReached}
          endRow={renderEndRow}
        />
      </Box>
      <Footer table={table} totalRoutes={archiveData?.count} />
      {contextMenu ? (
        <ArchiveRouteContextMenu contextMenu={contextMenu} setContextMenu={setContextMenu} />
      ) : null}
    </Stack>
  );
}

const ArchiveRouteContextMenu = ({
  contextMenu,
  setContextMenu,
}: {
  contextMenu: { x: number; y: number; routeUuid: string };
  setContextMenu: (value: { x: number; y: number; routeUuid: string } | null) => void;
}) => {
  const { selectedRouteUuids, addToSelection, removeFromSelection } = useRoutesTableStore(
    useShallow((state) => ({
      selectedRouteUuids: state.selectedRouteUuids,
      addToSelection: state.addToSelection,
      removeFromSelection: state.removeFromSelection,
    })),
  );

  const virtualElement = useVirtualElement({ ...contextMenu, type: "route" });
  const inSelectedSet = contextMenu.routeUuid && selectedRouteUuids.has(contextMenu.routeUuid);

  return (
    <ClickAwayListener onClickAway={() => setContextMenu(null)}>
      <Menu size="sm" anchorEl={virtualElement} open placement="bottom-start">
        {inSelectedSet && selectedRouteUuids.size > 1 ? (
          <ListItem>
            <ListItemContent sx={{ color: "var(--joy-palette-neutral-500)" }}>
              {selectedRouteUuids.size} routes selected
            </ListItemContent>
          </ListItem>
        ) : null}
        <MenuItem
          onClick={() => {
            if (!contextMenu) {
              return;
            }
            setContextMenu(null);
            inSelectedSet
              ? removeFromSelection([contextMenu.routeUuid])
              : addToSelection([contextMenu.routeUuid]);
          }}
        >
          <ListItemDecorator>{inSelectedSet ? <Deselect /> : <PlaylistAdd />}</ListItemDecorator>
          <ListItemContent>
            {inSelectedSet ? "Remove from selection" : "Add to selection"}
          </ListItemContent>
        </MenuItem>
        <ArchiveTableContextMenuContent
          onActionClick={() => setContextMenu(null)}
          routeUuid={contextMenu?.routeUuid}
        />
      </Menu>
    </ClickAwayListener>
  );
};

const ColumnSelector = ({ table }: { table: Table<Route> }) => {
  return (
    <Dropdown>
      <MenuButton
        slots={{ root: IconButton }}
        slotProps={{ root: { size: "sm" } }}
        color="secondary"
      >
        <ViewWeekOutlined />
      </MenuButton>
      <Menu placement="bottom-end">
        {table.getAllColumns().map((column) => {
          if (column.id === "selected") {
            return null;
          }
          return (
            <Checkbox
              key={column.id}
              checked={column.getIsVisible()}
              onChange={column.getToggleVisibilityHandler()}
              disabled={!column.getCanHide()}
              label={
                typeof column.columnDef.header === "string" ? column.columnDef.header : column.id
              }
              size="sm"
              sx={{
                paddingX: 1,
                paddingY: 0.5,
                display: "flex",
              }}
            />
          );
        })}
      </Menu>
    </Dropdown>
  );
};

const TableControls = ({ table }: { table: Table<Route> }) => {
  const { setDisplayFilterSidebar, displayFilterSidebar } = useArchiveUiStore();
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.75,
        padding: 0.75,
        paddingX: 1,
      }}
    >
      {/* <ViewModeIndicator /> */}
      <Box sx={{ flex: 1 }}>
        <ActiveFiltersIndicator />
      </Box>
      <Button
        variant="solid"
        color="secondary"
        size="sm"
        onClick={() => setDisplayFilterSidebar(!displayFilterSidebar)}
      >
        Filter...
      </Button>
      <ColumnSelector table={table} />
    </Box>
  );
};

const ArchiveTableContextMenuContent = ({
  routeUuid,
  onActionClick,
}: {
  routeUuid?: string;
  onActionClick?: () => void;
}) => {
  const selectedRouteUuids = useRoutesTableStore((state) => state.selectedRouteUuids);
  const routeUuids = useMemo(() => {
    if (routeUuid && !selectedRouteUuids.has(routeUuid)) {
      return [routeUuid];
    }
    return [...selectedRouteUuids];
  }, [routeUuid, selectedRouteUuids]);

  const { display: displayModal } = useModal();
  const exportDataMutation = useExportRoutesMutation();

  const onExportData = useCallback(() => {
    exportDataMutation.mutateAsync(routeUuids).then((result) => {
      const a = document.createElement("a");
      a.href = URL.createObjectURL(result.blob);
      a.download = result?.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
    });
    // TODO: Catch error and display a snackbar
    onActionClick?.();
  }, [exportDataMutation, onActionClick, routeUuids]);

  return (
    <>
      <MenuItem
        onClick={() => {
          displayModal("RESTORE_ARCHIVE_ROUTES", { routeUuids });
          onActionClick?.();
        }}
      >
        <ListItemDecorator>
          <RestoreFromTrash />
        </ListItemDecorator>
        <ListItemContent>Restore</ListItemContent>
      </MenuItem>
      <MenuItem onClick={onExportData}>
        <ListItemDecorator>
          <FileDownload />
        </ListItemDecorator>
        <ListItemContent>Export</ListItemContent>
      </MenuItem>
    </>
  );
};

const Footer = ({ table, totalRoutes }: { table: Table<Route>; totalRoutes?: number }) => {
  const query = useArchiveQueryContext();
  const selectedRouteUuidsCount = table.getSelectedRowModel().rows.length;

  return (
    <Box className="footer">
      <Box
        sx={{
          zIndex: 10,
          display: "flex",
          alignItems: "center",
          gap: 1,
          padding: 0.5,
          paddingX: 1,
          borderTop: "1px solid",
          borderColor: "neutral.200",
          backgroundColor: "neutral.100",
          justifyContent: selectedRouteUuidsCount ? "center" : "flex-start",
          paddingBottom: "calc(4px + env(safe-area-inset-bottom)) !important",
        }}
      >
        {selectedRouteUuidsCount ? (
          <>
            <Typography fontSize="sm" fontWeight="bold">
              {selectedRouteUuidsCount} of {totalRoutes} routes selected
            </Typography>
            <Dropdown>
              <MenuButton variant="solid" color="primary" size="sm">
                Actions...
              </MenuButton>
              <Menu size="sm">
                <ArchiveTableContextMenuContent />
              </Menu>
            </Dropdown>
          </>
        ) : totalRoutes === undefined ? (
          <CircularProgress size="sm" />
        ) : (
          <>
            {query.isFetching ? <CircularProgress size="sm" /> : null}
            <Typography fontSize="sm" fontWeight="bold">
              {totalRoutes} routes
            </Typography>
          </>
        )}
      </Box>
    </Box>
  );
};
